<template>
  <div class="flex h-full min-h-0 flex-col overflow-hidden border border-slate-600 bg-slate-950/60">
      <div class="z-40 flex h-9 flex-none items-center justify-end gap-2 border-b border-slate-700 bg-slate-900/95 px-3 text-xs shadow-lg">
        <span class="text-slate-500">Vertical zoom</span>
        <button type="button" class="h-6 w-7 rounded border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-40" :disabled="verticalZoom <= minVerticalZoom" title="Zoom out to show more notes" @click="zoomVertically(-1)">−</button>
        <button type="button" class="h-6 min-w-[52px] rounded border border-slate-600 bg-slate-800 px-2 text-slate-300 hover:bg-slate-700" title="Reset vertical zoom" @click="resetVerticalZoom">{{ verticalZoomPercent }}%</button>
        <button type="button" class="h-6 w-7 rounded border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-40" :disabled="verticalZoom >= maxVerticalZoom" title="Zoom in" @click="zoomVertically(1)">+</button>
      </div>
      <div class="flex flex-1 min-h-0">
        <div
          ref="minimapEl"
          class="relative w-28 flex-none touch-none select-none overflow-hidden border-r border-slate-700 bg-slate-950"
          title="Navigate the full piano roll"
          @pointerdown="startMinimapDrag"
          @pointermove="moveMinimapDrag"
          @pointerup="endMinimapDrag"
          @pointercancel="endMinimapDrag"
        >
          <canvas ref="minimapCanvas" class="absolute inset-0 h-full w-full pointer-events-none"></canvas>
          <div class="absolute inset-x-0 border border-cyan-200/90 bg-cyan-300/20 pointer-events-none" :style="minimapViewportStyle"></div>
          <div v-if="playing || playbackTimeMs > 0" class="absolute inset-x-0 h-px bg-white pointer-events-none" :style="minimapPlayheadStyle"></div>
        </div>
        <div class="flex flex-1 min-w-0 min-h-0 overflow-x-auto overflow-y-hidden">
          <div class="flex h-full min-h-0 flex-col" :style="{ width: `${rollWidth}px`, minWidth: `${rollWidth}px` }">
          <div class="z-30 flex flex-none border-b-2 border-cyan-300/70 bg-slate-900 shadow-lg" :style="{ height: `${keyboardHeight}px` }">
            <SynthPitchBar
              :minMidi="minMidi"
              :maxMidi="maxMidi"
              :selectedPitch="selectedPitch"
              :activeKeyColors="activeKeyColors"
              :playbackTimeMs="playbackTimeMs"
              :playing="playing"
              :keyWidth="keyWidth"
              :height="keyboardHeight"
              @select-pitch="$emit('select-pitch', $event)"
              @audition-pitch="$emit('audition-pitch', $event)"
            />
          </div>

          <div ref="noteViewport" class="relative flex-1 min-h-0" :class="playing ? 'overflow-hidden' : 'overflow-y-auto'" @scroll="updateNoteViewport">
            <div class="relative min-h-full" :style="{ height: `${contentHeight}px` }">
        <div class="absolute inset-x-0 top-0 z-20 border-t-2 border-cyan-300 pointer-events-none"></div>
        <div
          v-for="key in keys"
          :key="`column-${key.midi}`"
          class="absolute top-0 bottom-0 border-r border-slate-700/50"
          :class="key.isWhite ? 'bg-slate-800/15' : 'bg-slate-950/35'"
          :style="{ left: `${noteX(key.midi)}px`, width: `${key.width}px` }"
        ></div>
        <div
          v-for="line in timeLines"
          :key="line.timeMs"
          class="absolute left-0 right-0 border-t border-slate-600/35 pointer-events-none"
          :style="{ top: `${line.y}px` }"
        ></div>

        <button
          v-for="block in noteBlocks"
          :key="`${block.trackIndex}-${block.eventIndex}`"
          type="button"
          class="absolute z-10 rounded border text-[10px] font-medium text-slate-950 overflow-hidden transition-[filter]"
          :class="blockClass(block)"
          :style="block.style"
          :title="`${block.noteName} · ${ticksToNoteValue(block.durationTicks)}`"
          :disabled="playing || !block.visual.isEditable"
          @pointerdown="auditionNoteBlock(block)"
          @click="selectNoteBlock(block)"
        >
          <span class="block px-1 py-0.5 truncate">{{ block.noteName }}</span>
        </button>

        <div v-if="noteBlocks.length === 0" class="absolute inset-0 flex items-center justify-center text-xs text-slate-500">
          No notes in this playback scope
        </div>
            </div>
          </div>
          </div>
        </div>
      </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { midiToNoteName } from '../../stores/synthModel.js'
import { buildAbsoluteSequenceTiming, filterTimelineRange, pianoRollEventY, pianoRollSurfaceHeight } from './synthSequenceGeometry.js'
import { buildMinimapNoteGeometry, minimapViewportGeometry, minimapYToPlaybackMs, timeToMinimapY } from './synthPianoRollMinimapGeometry.js'
import { getSynthTrackColor, getSynthTrackVisualState } from './synthTrackColors.js'
import { ticksToNoteValue } from '../../stores/synthTiming.js'
import SynthPitchBar from './SynthPitchBar.vue'
import { buildPianoKeyGeometry } from './synthPianoKeyboardGeometry.js'
import { buildActiveKeyColorsFromPlayback } from './synthPlaybackHighlight.js'

const props = defineProps({
  tracks: { type: Array, required: true },
  trackIndices: { type: Array, required: true },
  playbackTimeMs: { type: Number, default: 0 },
  playing: { type: Boolean, default: false },
  selectedEventIndex: { type: Number, default: -1 },
  selectedPitch: { type: Number, default: null },
  editable: { type: Boolean, default: false },
  activeTrackIndex: { type: Number, default: null },
  minMidi: { type: Number, default: 24 },
  maxMidi: { type: Number, default: 84 },
  bpm: { type: Number, default: 120 },
})

const emit = defineEmits(['select-event', 'select-pitch', 'audition-pitch', 'audition-event', 'seek'])

const keyWidth = 28
const keyboardHeight = 58
const pixelsPerPitch = 24
const defaultPixelsPerMs = 0.22
const minVerticalZoom = 0.35
const maxVerticalZoom = 2
const verticalZoom = ref(1)
const pixelsPerMs = computed(() => defaultPixelsPerMs * verticalZoom.value)
const verticalZoomPercent = computed(() => Math.round(verticalZoom.value * 100))
const noteViewport = ref(null)
const minimapEl = ref(null)
const minimapCanvas = ref(null)
const minimapWidth = ref(0)
const minimapHeight = ref(0)
const viewportHeight = ref(0)
const viewportScrollTop = ref(0)
let resizeObserver = null
let minimapResizeObserver = null
let minimapDragging = false
let minimapSeekFrame = null
let pendingMinimapSeekMs = null
const pianoGeometry = computed(() => buildPianoKeyGeometry(props.minMidi, props.maxMidi, keyWidth))
const rollWidth = computed(() => pianoGeometry.value.width)
const keys = computed(() => pianoGeometry.value.keys)

const timedTracks = computed(() => props.tracks.map((track, localIndex) => ({
  track,
  trackIndex: props.trackIndices[localIndex] ?? localIndex,
  events: buildAbsoluteSequenceTiming(track?.sequence, props.bpm),
  visual: getSynthTrackVisualState(props.trackIndices[localIndex] ?? localIndex, props.activeTrackIndex, props.editable),
})))
const totalDurationMs = computed(() => Math.max(0, ...timedTracks.value.flatMap(item => item.events.map(event => event.endMs))))
const pitchRangeHeight = computed(() => keys.value.length * pixelsPerPitch)
const contentHeight = computed(() => pianoRollSurfaceHeight({
  playing: props.playing,
  viewportHeight: viewportHeight.value,
  totalDurationMs: totalDurationMs.value,
  pixelsPerMs: pixelsPerMs.value,
  pitchRangeHeight: pitchRangeHeight.value,
}))
const minimapContentHeight = computed(() => pianoRollSurfaceHeight({
  playing: false,
  viewportHeight: viewportHeight.value,
  totalDurationMs: totalDurationMs.value,
  pixelsPerMs: pixelsPerMs.value,
  pitchRangeHeight: pitchRangeHeight.value,
}))
const minimapPitchRange = computed(() => {
  const notes = timedTracks.value.flatMap(item => item.events.filter(event => event.note != null).map(event => event.note))
  return {
    min: notes.length ? Math.min(...notes) : props.minMidi,
    max: notes.length ? Math.max(...notes) : props.maxMidi,
  }
})
const minimapScrollTop = computed(() => props.playing
  ? props.playbackTimeMs * pixelsPerMs.value
  : viewportScrollTop.value)
const minimapViewport = computed(() => minimapViewportGeometry({
  scrollTop: minimapScrollTop.value,
  viewportHeight: viewportHeight.value,
  contentHeight: minimapContentHeight.value,
  minimapHeight: minimapHeight.value,
}))
const minimapViewportStyle = computed(() => ({
  top: `${minimapViewport.value.top}px`,
  height: `${minimapViewport.value.height}px`,
}))
const minimapPlayheadStyle = computed(() => ({
  top: `${timeToMinimapY(props.playbackTimeMs, totalDurationMs.value, minimapHeight.value)}px`,
}))
let editScrollTop = 0
const visibleRange = computed(() => props.playing
  ? { start: props.playbackTimeMs, end: props.playbackTimeMs + viewportHeight.value / pixelsPerMs.value }
  : { start: viewportScrollTop.value / pixelsPerMs.value, end: (viewportScrollTop.value + viewportHeight.value) / pixelsPerMs.value })

const activeKeyColors = computed(() => props.playing
  ? buildActiveKeyColorsFromPlayback(timedTracks.value, props.playbackTimeMs)
  : new Map())

const noteBlocks = computed(() => timedTracks.value.flatMap(({ trackIndex, events, visual }) => {
  const color = getSynthTrackColor(trackIndex)
  return filterTimelineRange(events, visibleRange.value.start, visibleRange.value.end, 1000).filter(event => event.note != null).map(event => {
    const y = pianoRollEventY(event.startMs, props.playing ? props.playbackTimeMs : 0, pixelsPerMs.value)
    const active = props.playing && visual.isActiveTrack && event.startMs <= props.playbackTimeMs && props.playbackTimeMs < event.endMs
    return {
      ...event,
      trackIndex,
      visual,
      active,
      noteName: midiToNoteName(event.note),
      style: {
        left: `${noteX(event.note) + 2}px`,
        top: `${y}px`,
        width: `${Math.max(8, noteWidth(event.note) - 4)}px`,
        height: `${Math.max(4, event.durationMs * pixelsPerMs.value)}px`,
        backgroundColor: color.solid,
        borderColor: active ? '#ffffff' : color.text,
        opacity: visual.opacity,
      },
    }
  })
}))

const timeLines = computed(() => {
  const lines = []
  const first = Math.floor(Math.max(0, visibleRange.value.start - 500) / 500) * 500
  const end = visibleRange.value.end + 500
  for (let timeMs = first; timeMs <= end; timeMs += 500) {
    lines.push({ timeMs, y: pianoRollEventY(timeMs, props.playing ? props.playbackTimeMs : 0, pixelsPerMs.value) })
  }
  return lines
})

function noteX(midi) {
  return pianoGeometry.value.keys.find(key => key.midi === midi)?.x ?? 0
}

function noteWidth(midi) {
  return pianoGeometry.value.keys.find(key => key.midi === midi)?.width ?? keyWidth
}

function zoomVertically(direction) {
  const next = verticalZoom.value + direction * 0.15
  verticalZoom.value = Math.min(maxVerticalZoom, Math.max(minVerticalZoom, Number(next.toFixed(2))))
}

function resetVerticalZoom() {
  verticalZoom.value = 1
}

function blockClass(block) {
  if (block.active) return 'ring-2 ring-white brightness-125'
  if (block.visual.isEditable && block.eventIndex === props.selectedEventIndex) return 'ring-2 ring-cyan-200'
  return ''
}

function selectNoteBlock(block) {
  if (props.playing || !block.visual.isEditable) return
  emit('select-event', block.eventIndex)
}

function auditionNoteBlock(block) {
  if (props.playing || !block.visual.isEditable) return
  emit('audition-event', { trackIndex: block.trackIndex, eventIndex: block.eventIndex })
}

function updateNoteViewport() {
  viewportScrollTop.value = noteViewport.value?.scrollTop ?? 0
}

function drawMinimap() {
  const canvas = minimapCanvas.value
  const width = minimapWidth.value
  const height = minimapHeight.value
  if (!canvas || width <= 0 || height <= 0) return

  const pixelRatio = window.devicePixelRatio || 1
  canvas.width = Math.round(width * pixelRatio)
  canvas.height = Math.round(height * pixelRatio)
  const context = canvas.getContext('2d')
  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  context.clearRect(0, 0, width, height)

  const geometry = buildMinimapNoteGeometry(timedTracks.value, {
    width,
    height,
    totalDurationMs: totalDurationMs.value,
    minPitch: minimapPitchRange.value.min,
    maxPitch: minimapPitchRange.value.max,
  })
  const trackOpacity = new Map(timedTracks.value.map(item => [item.trackIndex, item.visual.opacity]))
  for (const mark of geometry) {
    context.globalAlpha = trackOpacity.get(mark.trackIndex) ?? 1
    context.fillStyle = getSynthTrackColor(mark.trackIndex).solid
    context.fillRect(mark.x, mark.y, mark.width, mark.height)
  }
  context.globalAlpha = 1
}

function updateScrollFromMinimap(event) {
  const rect = minimapEl.value?.getBoundingClientRect()
  if (!rect || !noteViewport.value) return
  const targetPlaybackMs = minimapYToPlaybackMs(event.clientY - rect.top, rect.height, totalDurationMs.value)
  const scrollTop = Math.max(0, Math.min(
    minimapContentHeight.value - viewportHeight.value,
    targetPlaybackMs * pixelsPerMs.value - viewportHeight.value / 2,
  ))
  noteViewport.value.scrollTop = scrollTop
  viewportScrollTop.value = scrollTop
  queueMinimapSeek(targetPlaybackMs)
}

function queueMinimapSeek(positionMs) {
  pendingMinimapSeekMs = positionMs
  if (minimapSeekFrame != null) return
  minimapSeekFrame = requestAnimationFrame(() => {
    minimapSeekFrame = null
    emit('seek', pendingMinimapSeekMs)
  })
}

function startMinimapDrag(event) {
  if (event.button !== 0) return
  minimapDragging = true
  minimapEl.value?.setPointerCapture(event.pointerId)
  updateScrollFromMinimap(event)
}

function moveMinimapDrag(event) {
  if (!minimapDragging) return
  updateScrollFromMinimap(event)
}

function endMinimapDrag(event) {
  if (!minimapDragging) return
  minimapDragging = false
  minimapEl.value?.releasePointerCapture(event.pointerId)
  const rect = minimapEl.value?.getBoundingClientRect()
  if (rect) {
    if (minimapSeekFrame != null) cancelAnimationFrame(minimapSeekFrame)
    minimapSeekFrame = null
    pendingMinimapSeekMs = null
    emit('seek', minimapYToPlaybackMs(event.clientY - rect.top, rect.height, totalDurationMs.value))
  }
}

onMounted(async () => {
  await nextTick()
  resizeObserver = new ResizeObserver(entries => {
    viewportHeight.value = Math.max(0, entries[0]?.contentRect.height ?? 0)
  })
  if (noteViewport.value) resizeObserver.observe(noteViewport.value)
  minimapResizeObserver = new ResizeObserver(entries => {
    minimapWidth.value = Math.max(0, entries[0]?.contentRect.width ?? 0)
    minimapHeight.value = Math.max(0, entries[0]?.contentRect.height ?? 0)
  })
  if (minimapEl.value) minimapResizeObserver.observe(minimapEl.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  minimapResizeObserver?.disconnect()
  if (minimapSeekFrame != null) cancelAnimationFrame(minimapSeekFrame)
})

watch([timedTracks, minimapWidth, minimapHeight, totalDurationMs, minimapPitchRange], async () => {
  await nextTick()
  drawMinimap()
}, { immediate: true })

watch(() => props.playing, async (playing, wasPlaying) => {
  if (playing && !wasPlaying) {
    editScrollTop = noteViewport.value?.scrollTop ?? 0
    return
  }
  if (!playing && wasPlaying) {
    await nextTick()
    if (noteViewport.value) {
      noteViewport.value.scrollTop = editScrollTop
      viewportScrollTop.value = editScrollTop
    }
  }
})
</script>
